import ANTENNAS from "./antennas.json";
import { isRinexObsText, trimRinexObs } from "./rinex-trim.js";

const TRIM_START_SEC = 120;
const TRIM_END_SEC = 120;

// Only plain-text RINEX obs files can be trimmed this way — vendor binary formats
// (.T02/.T01/.T04/.dat) pass through untouched (the extension check is just a cheap
// skip; the real gate is isRinexObsText() sniffing the actual file content below).
function looksLikeRinexObsFilename(name) {
  return /\.\d{2}o$/i.test(name) || /\.(rnx|obs)$/i.test(name);
}

// Every incoming RINEX obs file gets 2 minutes trimmed off both ends before it lands
// in the DAM (settling/pack-up time at the start/end of a static occupation) — ported
// from SafeHarborBase.exe's TrimOperation, which can't run in a browser or headless on
// this server (full WPF desktop app). Runs once, at upload time, so a file already in
// the DAM never gets re-trimmed on a later submit.
async function maybeTrimForOpus(file) {
  if (!looksLikeRinexObsFilename(file.name)) return { file, trimInfo: null };
  let text;
  try { text = await file.text(); } catch { return { file, trimInfo: null }; }
  if (!isRinexObsText(text)) return { file, trimInfo: null };
  let result;
  try {
    result = trimRinexObs(text, { trimStartSec: TRIM_START_SEC, trimEndSec: TRIM_END_SEC });
  } catch (err) {
    return { file, trimInfo: { error: String(err?.message || err) } };
  }
  if (!result.trimmed) return { file, trimInfo: { skipped: true, reason: result.reason } };
  return {
    file: new File([result.text], file.name, { type: file.type || "text/plain" }),
    trimInfo: result,
  };
}

// Track the last project ID broadcast so tools opened after the event can still pick it up.
let _lastBroadcastProjectId = "";
document.addEventListener("hydrotools:project-changed", (e) => {
  _lastBroadcastProjectId = String(e.detail?.projectId || "");
});

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function fmtBytes(b) {
  const n = Number(b) || 0;
  if (n >= 1048576) return (n / 1048576).toFixed(1) + " MB";
  if (n >= 1024) return (n / 1024).toFixed(0) + " KB";
  return n + " B";
}

function fmtWhen(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Every Safe Harbor marina survey uses the same gear: a Trimble R10 (integrated
// antenna, IGS code below) set up with its ARP directly over the mark.
const SAFE_HARBOR_ANT_CODE = "TRMR10          NONE";
const SAFE_HARBOR_ANT_LABEL = "Trimble R10 (TRMR10 NONE)";
const SAFE_HARBOR_HEIGHT_M = "0.0";

const STATUS_LABEL = {
  submitted: "Submitted — awaiting OPUS",
  submit_failed: "Submission failed",
  completed: "Completed",
};
const STATUS_COLOR = {
  submitted: "#f59e0b",
  submit_failed: "#ef4444",
  completed: "#22c55e",
};

function createOpusGnssTool(app) {
  let containerEl = null;
  let currentMarinaId = "";
  let files = []; // gnss-raw category assets for the current project
  let filesLoading = false;
  let jobs = [];
  let jobsLoading = false;
  let selectedKey = "";
  let antQuery = "";
  let antCode = ""; // exact fixed-width code that gets submitted
  let heightM = "";
  let notifyEmail = ""; // optional override; blank = auto-generated results+jobid@ address
  let submitting = false;
  let uploadPct = null; // 0-100 while a fresh file upload is in flight, else null
  let jobsPollTimer = null;

  function antennaMatches(query) {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const hits = [];
    for (const a of ANTENNAS) {
      if (a.code.toLowerCase().includes(q) || a.title.toLowerCase().includes(q)) {
        hits.push(a);
        if (hits.length >= 25) break;
      }
    }
    return hits;
  }

  // Non-Safe-Harbor clients (e.g. OnX) don't run this GNSS workflow at all today, but
  // default to true when a project can't be found yet (catalog still loading) since
  // "safeharbor" is also the fallback clientId normalizeProject() itself applies.
  function isSafeHarborProject(marinaId) {
    if (!marinaId) return false;
    const projects = app.services.context?.getAllProjects?.() || [];
    const proj = projects.find((p) => p.marinaId === marinaId);
    return !proj || proj.clientId === "safeharbor";
  }

  function applySafeHarborDefaults(marinaId) {
    if (!isSafeHarborProject(marinaId)) return;
    if (!antCode) {
      antCode = SAFE_HARBOR_ANT_CODE;
      antQuery = SAFE_HARBOR_ANT_LABEL;
    }
    if (heightM === "") heightM = SAFE_HARBOR_HEIGHT_M;
  }

  async function loadFiles(code) {
    filesLoading = true;
    render();
    try {
      const resp = await fetch(`/api/project/assets?code=${encodeURIComponent(code)}&category=gnss-raw`);
      const data = await resp.json();
      files = Array.isArray(data.files) ? data.files : [];
      if (!files.some((f) => f.key === selectedKey)) selectedKey = files[0]?.key || "";
    } catch {
      files = [];
    }
    filesLoading = false;
    render();
  }

  async function loadJobs(code) {
    jobsLoading = true;
    render();
    try {
      const resp = await fetch(`/api/project/opus/jobs?code=${encodeURIComponent(code)}`);
      const data = await resp.json();
      jobs = Array.isArray(data.jobs) ? data.jobs : [];
    } catch {
      jobs = [];
    }
    jobsLoading = false;
    render();
    ensureJobsPolling();
  }

  // While anything is still "submitted" (awaiting the OPUS result email), poll the job
  // list so a completed result shows up without the user manually refreshing.
  function ensureJobsPolling() {
    const hasPending = jobs.some((j) => j.status === "submitted");
    if (hasPending && !jobsPollTimer) {
      jobsPollTimer = setInterval(() => { if (currentMarinaId) loadJobs(currentMarinaId); }, 20000);
    } else if (!hasPending && jobsPollTimer) {
      clearInterval(jobsPollTimer);
      jobsPollTimer = null;
    }
  }

  async function uploadNewFile(rawFile) {
    if (!currentMarinaId) { app.ui.toast("Select a project first."); return; }
    uploadPct = 0;
    render();
    try {
      const { file, trimInfo } = await maybeTrimForOpus(rawFile);
      if (trimInfo?.error) {
        app.ui.toast(`Could not check ${rawFile.name} for trimming (${trimInfo.error}) — uploading as-is.`);
      } else if (trimInfo?.skipped) {
        app.ui.toast(`${rawFile.name}: skipped the 2-minute trim (${trimInfo.reason}) — uploading untrimmed.`);
      } else if (trimInfo?.trimmed) {
        app.ui.toast(`Trimmed 2 min from each end of ${rawFile.name} (${trimInfo.originalEpochs} → ${trimInfo.keptEpochs} epochs) before upload.`);
      }
      const presignResp = await fetch("/api/project/asset/presign-upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: currentMarinaId, category: "gnss-raw", filename: file.name, contentType: file.type || "application/octet-stream" }),
      });
      const presignData = await presignResp.json();
      if (!presignData.url) throw new Error(presignData.error || "Presign failed.");
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presignData.url);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) { uploadPct = Math.round((e.loaded / e.total) * 100); render(); } };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`Upload failed: HTTP ${xhr.status}`));
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.send(file);
      });
      uploadPct = null;
      await loadFiles(currentMarinaId);
      selectedKey = presignData.key;
      app.ui.toast(`Uploaded ${file.name}.`);
      render();
    } catch (err) {
      uploadPct = null;
      app.ui.toast(`Upload failed: ${err.message}`);
      render();
    }
  }

  async function submitToOpus() {
    if (!currentMarinaId || !selectedKey || !antCode || submitting) return;
    const h = Number(heightM);
    if (!Number.isFinite(h) || h < 0 || h > 50) { app.ui.toast("Enter a valid ARP height in meters (0-50)."); return; }
    submitting = true;
    render();
    try {
      const resp = await fetch("/api/project/opus/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: currentMarinaId, s3Key: selectedKey, antType: antCode, heightM: h, notifyEmail: notifyEmail.trim() }),
      });
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || "Submission failed.");
      app.ui.toast(notifyEmail.trim()
        ? `Submitted to OPUS — results will be emailed to ${notifyEmail.trim()} directly (won't auto-complete in this tool).`
        : "Submitted to OPUS — results are emailed back automatically, usually within a few minutes to an hour.");
      await loadJobs(currentMarinaId);
    } catch (err) {
      app.ui.toast(`OPUS submission failed: ${err.message}`);
    }
    submitting = false;
    render();
  }

  function onProjectChanged(e) {
    const projectId = String(e.detail?.projectId || "");
    currentMarinaId = projectId;
    selectedKey = "";
    if (projectId) {
      applySafeHarborDefaults(projectId);
      loadFiles(projectId);
      loadJobs(projectId);
    } else {
      files = [];
      jobs = [];
      render();
    }
  }

  function mount(nextContainer, { initialProjectId = "" } = {}) {
    containerEl = nextContainer;
    if (initialProjectId) currentMarinaId = initialProjectId;
    else if (!currentMarinaId && _lastBroadcastProjectId) currentMarinaId = _lastBroadcastProjectId;
    document.addEventListener("hydrotools:project-changed", onProjectChanged);
    if (currentMarinaId) {
      applySafeHarborDefaults(currentMarinaId);
      loadFiles(currentMarinaId);
      loadJobs(currentMarinaId);
    }
    render();
  }

  function destroy() {
    document.removeEventListener("hydrotools:project-changed", onProjectChanged);
    if (jobsPollTimer) { clearInterval(jobsPollTimer); jobsPollTimer = null; }
  }

  function renderJobRow(job) {
    const color = STATUS_COLOR[job.status] || "#94a3b8";
    const label = STATUS_LABEL[job.status] || job.status;
    const r = job.result;
    return `
      <div style="border:1px solid rgba(148,163,184,0.18); border-radius:6px; padding:8px 10px; margin-bottom:6px;">
        <div style="display:flex; justify-content:space-between; align-items:center; gap:8px; flex-wrap:wrap;">
          <span style="font-weight:600;">${esc(job.originalName)}</span>
          <span class="small" style="color:${color};">&#9679; ${esc(label)}</span>
        </div>
        <div class="small" style="color:var(--muted); margin-top:2px;">
          Submitted ${esc(fmtWhen(job.submittedAt))} &middot; ant ${esc(job.antType?.trim() || "")} &middot; height ${esc(job.heightM)}m
          ${job.completedAt ? ` &middot; completed ${esc(fmtWhen(job.completedAt))}` : ""}
        </div>
        ${job.customEmail ? `<div class="small" style="color:#f59e0b; margin-top:2px;">Results sent directly to ${esc(job.emailAddress)} — won't auto-complete here.</div>` : ""}
        ${job.status === "submit_failed" ? `<div class="small" style="color:#ef4444; margin-top:4px;">HTTP ${esc(job.opusHttpStatus)} — OPUS did not accept the upload.</div>` : ""}
        ${!r && job.submitResponseSnippet ? `
          <details style="margin-top:4px;">
            <summary class="small" style="cursor:pointer; color:var(--muted);">OPUS upload response (HTTP ${esc(job.opusHttpStatus)})</summary>
            <pre class="small" style="white-space:pre-wrap; margin-top:4px; padding:6px; background:rgba(2,6,23,0.35); border-radius:4px; max-height:220px; overflow:auto;">${esc(job.submitResponseSnippet)}</pre>
          </details>
        ` : ""}
        ${r ? `
          <div class="small" style="margin-top:6px; display:grid; grid-template-columns:auto 1fr; gap:2px 10px;">
            ${r.refFrame ? `<span style="color:var(--muted);">Ref frame</span><span>${esc(r.refFrame)}</span>` : ""}
            ${r.lat ? `<span style="color:var(--muted);">Latitude</span><span>${esc(r.lat)}</span>` : ""}
            ${r.lon ? `<span style="color:var(--muted);">Longitude</span><span>${esc(r.lon)}</span>` : ""}
            ${r.ellipHeightM ? `<span style="color:var(--muted);">Ellip. height (m)</span><span>${esc(r.ellipHeightM)}</span>` : ""}
            ${r.overallRmsM ? `<span style="color:var(--muted);">Overall RMS (m)</span><span>${esc(r.overallRmsM)}</span>` : ""}
          </div>
          <details style="margin-top:6px;">
            <summary class="small" style="cursor:pointer; color:var(--muted);">Raw OPUS report</summary>
            <pre class="small" style="white-space:pre-wrap; margin-top:4px; padding:6px; background:rgba(2,6,23,0.35); border-radius:4px; max-height:220px; overflow:auto;">${esc(r.rawText)}</pre>
          </details>
        ` : ""}
      </div>
    `;
  }

  function render() {
    if (!containerEl) return;
    const antHits = antennaMatches(antQuery);
    const canSubmit = !!(currentMarinaId && selectedKey && antCode && heightM !== "" && !submitting);

    containerEl.innerHTML = `
      <div class="small" style="color:var(--muted); padding-bottom:8px;">
        Submit a GNSS observation file (Trimble .T02/.T01/.T04, RINEX, etc.) to NGS OPUS for
        post-processing. Results are captured automatically when NGS emails them back —
        no manual email round-trip.
      </div>

      <div class="subtoolset-list" style="--subtoolset-bleed-top:0px;">
        <details class="toolset-group" open>
          <summary>Submit Observation</summary>
          <div class="toolset-content">
            ${!currentMarinaId ? `<div class="small" style="color:var(--muted);">Select an active project in Project Manager first.</div>` : `
              <div class="row" style="gap:8px; align-items:center; flex-wrap:wrap;">
                <button id="opus-upload-btn" class="btn" ${uploadPct != null ? "disabled" : ""}>${uploadPct != null ? `Uploading ${uploadPct}%...` : "+ Upload File"}</button>
                <input id="opus-file-input" type="file" accept=".t02,.t01,.t04,.dat,.rnx,.o,.zip,.gz,.Z" style="display:none;" />
                <select id="opus-file-select" style="flex:1; min-width:200px;">
                  ${filesLoading ? `<option value="">Loading files...</option>` : !files.length ? `<option value="">No files uploaded yet</option>` : files.map((f) => `<option value="${esc(f.key)}" ${f.key === selectedKey ? "selected" : ""}>${esc(f.name)} (${esc(fmtBytes(f.size))})</option>`).join("")}
                </select>
              </div>

              <div class="row" style="margin-top:10px; gap:8px; align-items:flex-start; flex-wrap:wrap;">
                <div style="flex:1; min-width:240px; position:relative;">
                  <label class="small" style="color:var(--muted);">Antenna type</label>
                  <input id="opus-ant-search" type="text" placeholder="Search e.g. Trimble R8, Zephyr..." value="${esc(antQuery)}" style="width:100%;" autocomplete="off" />
                  ${antCode ? `<div class="small" style="margin-top:2px; color:#22c55e;">Selected: <code>${esc(antCode)}</code></div>` : ""}
                  ${antQuery && antHits.length ? `
                    <div style="position:absolute; z-index:20; top:100%; left:0; right:0; max-height:220px; overflow:auto; background:var(--panel-bg, #0f172a); border:1px solid rgba(148,163,184,0.3); border-radius:6px; margin-top:2px;">
                      ${antHits.map((a) => `<div data-opus-ant-pick="${esc(a.code)}" class="small" style="padding:5px 8px; cursor:pointer; border-bottom:1px solid rgba(148,163,184,0.1);" title="${esc(a.code)}">${esc(a.title)}</div>`).join("")}
                    </div>
                  ` : ""}
                </div>
                <div style="width:120px;">
                  <label class="small" style="color:var(--muted);">ARP height (m)</label>
                  <input id="opus-height" type="number" min="0" max="50" step="0.001" value="${esc(heightM)}" style="width:100%;" />
                </div>
              </div>

              <div class="row" style="margin-top:10px;">
                <div style="flex:1; min-width:240px;">
                  <label class="small" style="color:var(--muted);">Notify email (optional — overrides automatic capture)</label>
                  <input id="opus-notify-email" type="email" placeholder="you@company.com — leave blank for automatic capture" value="${esc(notifyEmail)}" style="width:100%;" autocomplete="off" />
                </div>
              </div>

              <div class="row" style="margin-top:10px;">
                <button id="opus-submit-btn" class="btn" ${canSubmit ? "" : "disabled"} style="${canSubmit ? "background:rgba(34,197,94,0.18);border-color:#22c55e;color:#22c55e;" : ""}">${submitting ? "Submitting..." : "Submit to OPUS"}</button>
              </div>
            `}
          </div>
        </details>

        <details class="toolset-group" open>
          <summary>Submission History</summary>
          <div class="toolset-content">
            ${!currentMarinaId ? "" : jobsLoading && !jobs.length ? `<div class="small" style="color:var(--muted);">Loading...</div>` : !jobs.length ? `<div class="small" style="color:var(--muted);">No OPUS submissions yet for this project.</div>` : jobs.map(renderJobRow).join("")}
          </div>
        </details>
      </div>
    `;

    const uploadBtn = containerEl.querySelector("#opus-upload-btn");
    const fileInput = containerEl.querySelector("#opus-file-input");
    const fileSelect = containerEl.querySelector("#opus-file-select");
    const antSearch = containerEl.querySelector("#opus-ant-search");
    const heightInput = containerEl.querySelector("#opus-height");
    const notifyEmailInput = containerEl.querySelector("#opus-notify-email");
    const submitBtn = containerEl.querySelector("#opus-submit-btn");

    uploadBtn?.addEventListener("click", () => fileInput?.click());
    fileInput?.addEventListener("change", () => {
      const f = fileInput.files?.[0];
      fileInput.value = "";
      if (f) uploadNewFile(f);
    });
    fileSelect?.addEventListener("change", () => { selectedKey = fileSelect.value; });
    antSearch?.addEventListener("input", () => {
      antQuery = antSearch.value;
      antCode = ""; // typing again means the previous pick no longer applies
      render();
      containerEl.querySelector("#opus-ant-search")?.focus();
    });
    containerEl.querySelectorAll("[data-opus-ant-pick]").forEach((el) => {
      el.addEventListener("click", () => {
        antCode = el.getAttribute("data-opus-ant-pick") || "";
        antQuery = el.textContent || "";
        render();
      });
    });
    heightInput?.addEventListener("input", () => { heightM = heightInput.value; });
    notifyEmailInput?.addEventListener("input", () => { notifyEmail = notifyEmailInput.value; });
    submitBtn?.addEventListener("click", submitToOpus);
  }

  return { mount, destroy };
}

export const opusGnssTool = {
  id: "opus-gnss",
  name: "OPUS GNSS Solutions",
  icon: "&#128752;",
  init(app) {
    this._rt = this._rt ?? createOpusGnssTool(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createOpusGnssTool(app);
    this._rt.mount(containerEl);
  },
};
